import React from "react";

import type { Time } from "~/tools/date-time/time";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";
import type { ExternalComponent, FullComponent } from "../common/openapi";

import { useExplorationDrawerState } from "./react/drawer/use-exploration-drawer-state";

const getExternalComponent = (externalComponentId: undefined | string, synced: ReadonlyArray<ExternalComponent>) =>
	synced.find(({ externalId = "" }) => externalId === externalComponentId);

export interface ComponentActions {
	showLastChange: undefined | VoidFunction;
	showStatus: undefined | VoidFunction;
	showConfiguration: undefined | VoidFunction;
	showLogs: undefined | React.Dispatch<ComponentActionsTimeProps>;
}

export interface ComponentActionsTimeProps {
	timestamp?: Time;
	timeRange: TimeRange;
}

export const useComponentActions = (component: FullComponent): ComponentActions => {
	const explorationDrawer = useExplorationDrawerState();

	const externalComponent = React.useMemo(
		() => getExternalComponent(component?.provisioning?.externalId, component.synced),
		[component],
	);

	return {
		showLastChange:
			component?.provisioning?.showConfiguration && externalComponent?.sourceProperties
				? () => {
						explorationDrawer.openComponentLastChange({
							component,
							sourceProperties: externalComponent?.sourceProperties,
							lastUpdateTimestamp: externalComponent?.lastUpdateTimestamp,
						});
					}
				: undefined,

		showStatus:
			component?.provisioning?.showStatus && externalComponent?.status != null
				? () => {
						explorationDrawer.openComponentStatus({
							component,
							status: externalComponent.status!,
						});
					}
				: undefined,

		showConfiguration:
			component?.provisioning?.showConfiguration && externalComponent?.sourceProperties != null
				? () => {
						explorationDrawer.openComponentConfiguration({
							component,
							configuration: externalComponent.sourceProperties!,
						});
					}
				: undefined,

		showLogs: component?.highlights?.showLogs
			? ({ timestamp, timeRange }) => {
					explorationDrawer.openComponentLogs({
						component,
						timestamp,
						timeRange,
					});
				}
			: undefined,
	};
};
